/**
 * @render react
 * @name MultiPageNavigation
*/

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash.find';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withPolaris } from 'Components/protons/Polaris';
import { withSticky } from 'Components/protons/withSticky';

import { Button } from 'Components/atoms/Button';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * MultiPageNavigation component
 *
 * @param {array} props.content - Array containing objects with the following keys: url, title, subtitle, excerpt, image
 * @param {string} props.layout - Either default or expanded
 * @param {string} props.allPagesLabel - Label for see all pages
 * @param {number} props.visibleItems - Number of visible items when layout is expanded. Possible values 3,4,5 for now.
 * @param {bool} props.showIndex - To show the page index after the title
 * @param {bool} props.showIndexNumber - To show the page index as number before the title
 * @param {bool} props.showCurrentlyReadingText - To show the current label
 * @param {bool} props.showCurrentlyReadingOnElement - To show the current label on the relevant element
 * @param {string} props.selectedUrl - Selected url among the items
 * @param {string} props.currentLabel - Label for the currently read article
 * @param {number} props.stickyStatus - This comes from the withSticky HOC and must not be provided explicitly.
 * @param {bool} props.enableToggling - checks if toggling is enabled
 * @param {bool} props.showSeeLess - checks if See Less label is enabled
 * @param {string} props.seeLessLabel - Label for see less pages
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {Object} ampScripts — Amp scripts to be added in the head via Helmet
 */
const MultiPageNavigation = ({
  allPagesLabel,
  content,
  context: { cssPrefix, platform },
  currentLabel,
  enableToggling,
  extraClassNames,
  layout,
  seeLessLabel,
  selectedUrl,
  showCurrentlyReadingOnElement,
  showCurrentlyReadingText,
  showIndex,
  showIndexNumber,
  showSeeLess,
  stickyStatus,
  visibleItems,
}) => {
  // For opening and closing the menu
  const [openMenu, setOpenMenu] = useState((layout === 'expanded') || false);
  const [userSelectionOpen, setUserSelectionOpen] = useState(false);

  // For expanding the menu
  const [expanded, setExpanded] = useState(false);

  /**
   * Toggle the menu
   */
  const toggleMenu = () => {
    if (enableToggling) {
      setOpenMenu(!openMenu);
      setUserSelectionOpen(!userSelectionOpen);
    }
  };

  /**
   * Toggle the expand
   */
  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  /**
   * When the sticky status changes, we open or close the menu
   */
  useEffect(() => {
    if (layout === 'expanded') {
      // If component is sticky, we close the menu
      if (stickyStatus === 2) {
        setOpenMenu(false);
      } else if (stickyStatus === 0) {
        if (userSelectionOpen) {
          setOpenMenu(true);
        }
      }
    }
  }, [stickyStatus]);

  // Check if we have a selected URL
  const selectedItemIndex = content.findIndex((item) => {
    if (item.url === selectedUrl) return true;

    const canonicalItemMeta = find(item.metaData ? item.metaData.meta : {}, { key: 'canonical' }, {});
    return canonicalItemMeta && canonicalItemMeta.value === selectedUrl;
  });
  const selectedItem = content[selectedItemIndex];
  const totalItems = content.length;

  if (!selectedItem) {
    return null;
  }

  return (
    <>
      {platform === 'amp' && (
        <>
          <amp-state id="openMenu">
            <script type="application/json">
              {layout === 'expanded' ? 'true' : 'false'}
            </script>
          </amp-state>

          <amp-state id="expandedMenu">
            <script type="application/json">
              {layout === 'expanded' && expanded ? 'true' : 'false'}
            </script>
          </amp-state>
        </>
      )}

      <div
        className={classNames({
          [`${cssPrefix}__multi-page-navigation`]: true,
          '-is-open': openMenu,
          '-is-close': !openMenu,
          '-is-expanded': (layout === 'expanded' && expanded),
          '-is-partly-expanded': (layout === 'expanded' && !expanded),
          [`-layout-${layout}`]: true,
          [`-visible-${visibleItems}`]: (layout === 'expanded'),
          ...extraClassNames,
        })}
        // eslint-disable-next-line
        data-amp-bind-class={platform === 'amp' ? `(openMenu ? '-is-open': '-is-close') + (expandedMenu ? ' -is-expanded': ' -is-partly-expanded') + ' ${cssPrefix}__multi-page-navigation -layout-${layout}'` : undefined}
      >
        <Button
          onClick={toggleMenu}
          ampOn="tap:AMP.setState({ openMenu: !openMenu })"
        >
          <span className={`${cssPrefix}__multi-page-navigation--title`}>
            <span className={`${cssPrefix}__multi-page-navigation--index-number`}>{showIndexNumber && `${selectedItemIndex + 1}. `}</span>
            {selectedItem.title}
          </span>
          <span className={`${cssPrefix}__multi-page-navigation--toggle-index`}>
            {showIndex && (`Page ${selectedItemIndex + 1} of ${totalItems}`)}

            {showCurrentlyReadingText && (
              <span className={`${cssPrefix}__multi-page-navigation--current-label`}>{currentLabel}</span>
            )}
            {enableToggling && (
              <i className={`${cssPrefix}__multi-page-navigation--arrow`} />
            )}
          </span>
        </Button>
        <div className={`${cssPrefix}__multi-page-navigation--menu`}>
          <ul>
            {content.map((singleContent, index) => (
              <li key={index} className={(singleContent.url === selectedUrl && index === 0) ? '-hidden' : ''}>
                <Link href={singleContent.url} extraClassNames={{ '-current': singleContent.url === selectedUrl }} onClick={toggleMenu}>
                  <span>
                    <span className={`${cssPrefix}__multi-page-navigation--index-number`}>{showIndexNumber && `${index + 1}. `}</span>
                    {singleContent.title}
                  </span>

                  {showCurrentlyReadingOnElement && singleContent.url === selectedUrl && (
                    <span className={`${cssPrefix}__multi-page-navigation--current-label`}>
                      {currentLabel}
                    </span>
                  )}

                  {showIndex && (
                    <span className={`${cssPrefix}__multi-page-navigation--index`}>{`Page ${index + 1} of ${totalItems}`}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>

          {layout === 'expanded' && totalItems > visibleItems && openMenu && ((!expanded && !showSeeLess) || showSeeLess) && (
            <Button
              onClick={toggleExpand}
              ampOn="tap:AMP.setState({ expandedMenu: !expandedMenu })"
              extraClassNames={{
                '-toggle-bottom': true,
              }}
            >
              <span>{expanded ? seeLessLabel : allPagesLabel}</span>

              <i className={`${cssPrefix}__multi-page-navigation--arrow`} />
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

/**
 * Valid prop types
 */
MultiPageNavigation.propTypes = {
  allPagesLabel: PropTypes.string,
  ampScripts: PropTypes.shape(), // eslint-disable-line
  content: PropTypes.arrayOf(PropTypes.shape()),
  context: PropTypes.shape(),
  currentLabel: PropTypes.string,
  enableToggling: PropTypes.bool,
  extraClassNames: PropTypes.instanceOf(Object),
  layout: PropTypes.string,
  seeLessLabel: PropTypes.string,
  selectedUrl: PropTypes.string,
  showCurrentlyReadingOnElement: PropTypes.bool,
  showCurrentlyReadingText: PropTypes.bool,
  showIndex: PropTypes.bool,
  showIndexNumber: PropTypes.bool,
  showSeeLess: PropTypes.bool,
  stickyStatus: PropTypes.number, // from withSticky HOC
  visibleItems: PropTypes.number,
};

/**
 * Default props
 */
MultiPageNavigation.defaultProps = {
  allPagesLabel: 'See all pages',
  ampScripts: {
    'amp-bind': 'https://cdn.ampproject.org/v0/amp-bind-0.1.js',
  },
  content: [],
  context: {},
  currentLabel: 'Currently reading',
  enableToggling: true,
  extraClassNames: {},
  layout: 'default',
  seeLessLabel: 'See less pages',
  selectedUrl: '',
  showCurrentlyReadingOnElement: false,
  showCurrentlyReadingText: false,
  showIndex: true,
  showIndexNumber: false,
  showSeeLess: false,
  stickyStatus: 0,
  visibleItems: 5,
};

export default compose(withPolaris, withSticky)(MultiPageNavigation);
